import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getImageList } from "../components/PropertyCard";
import Modal from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import AddProperty from "./AddProperty";
import { getPropertyById, submitInquiry } from "../services/api";
import { decodeId } from "../utils/idEncoder";

function normalizeProperty(data) {
  if (!data) {
    return null;
  }

  return {
    id: data.id,
    title: data.title || data.TagLine || data.property_title || "Property Listing",
    description: data.description || data.OtherDetails || "No description provided.",
    type: String(data.type || data.PropertyType || "house").toLowerCase(),
    listingType: String(data.listing_type || data.ListingType || "sale").toLowerCase() === "rent" ? "rent" : "sale",
    status: data.status || "published",
    price: data.price ?? data.Price ?? 0,
    city: data.city || data.City || "-",
    district: data.district || data.District || data.city || data.City || "-",
    locationLabel: data.location_label || data.Location || data.locality || "-",
    latitude: data.latitude ?? null,
    longitude: data.longitude ?? null,
    bedrooms: data.bedrooms ?? data.NumberOfRooms ?? 0,
    bathrooms: data.bathrooms ?? data.NumberOfWashrooms ?? 0,
    areaSqft: data.area_sqft ?? data.Area ?? null,
    areaValue: data.area_value ?? null,
    areaUnit: data.area_unit ?? null,
    shopUnits: data.shop_units ?? data.NumberOfShops ?? 0,
    contactName: data.contact_name || "",
    contactPhone: data.contact_phone || "",
    contactAdminEmail: data.contact_admin_email || null,
    imageList: Array.isArray(data.property_images) ? data.property_images : data.property_images ? [data.property_images] : []
  };
}

export default function PropertyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, user, isAuthenticated } = useAuth();

  const numericId = decodeId(id);
  const isApiPropertyId = numericId !== null && numericId > 0;

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inquiryMessage, setInquiryMessage] = useState("");
  const [inquiryStatus, setInquiryStatus] = useState("");
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        if (!isApiPropertyId) {
          setProperty(null);
          setError("Property not found.");
          return;
        }

        const data = await getPropertyById(numericId);
        setProperty(normalizeProperty(data));
        setActiveImageIndex(0);
      } catch {
        setProperty(null);
        setError("Property not found.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, isApiPropertyId, numericId]);

  const mapEmbedUrl = useMemo(() => {
    if (property?.latitude == null || property?.longitude == null) {
      return "";
    }
    return `https://www.google.com/maps?q=${property.latitude},${property.longitude}&z=15&output=embed`;
  }, [property?.latitude, property?.longitude]);

  if (loading) {
    return <div className="container">Loading property...</div>;
  }

  if (!property) {
    return <div className="container">Property not found.</div>;
  }

  const canSeeMap = role === "agent" || role === "admin";
  const canSubmitInquiry = isApiPropertyId;
  const canEditListing = role === "admin";
  const galleryImages = getImageList(property.imageList, property.type);
  const thumbnailImages = galleryImages.slice(0, 5);
  const editPrefill = property ? {
    title: property.title,
    description: property.description,
    type: property.type,
    listing_type: property.listingType,
    contact_name: property.contactName,
    contact_phone: property.contactPhone,
    price: property.price,
    city: property.city,
    district: property.district,
    location_label: property.locationLabel,
    latitude: property.latitude,
    longitude: property.longitude,
    bedrooms: property.bedrooms,
    bathrooms: property.bathrooms,
    area_value: property.areaValue,
    area_unit: property.areaUnit,
    shop_units: property.shopUnits,
    property_images: property.imageList
  } : null;

  const onEditSuccess = (updated, message) => {
    setProperty(normalizeProperty(updated));
    setEditOpen(false);
    setError("");
    setInquiryStatus(message || "Property updated successfully.");
  };

  const onSendInquiry = async () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/property/${id}` } });
      return;
    }

    if (!canSubmitInquiry) {
      setInquiryStatus("Inquiry is available for live API listings only.");
      return;
    }

    setInquiryStatus("");
    try {
      setInquiryLoading(true);
      const defaultMessage = `Inquiry from ${user?.full_name || "user"} for property #${property.id}`;
      await submitInquiry(numericId, inquiryMessage.trim() || defaultMessage);
      setInquiryMessage("");
      setInquiryStatus("Inquiry sent to admin successfully.");
    } catch (requestError) {
      const message = requestError?.response?.data?.detail || "Failed to send inquiry.";
      setInquiryStatus(message);
    } finally {
      setInquiryLoading(false);
    }
  };

  return (
    <div className="container page">
      <div className="details-wrapper">
        {error ? <div className="feedback-banner error-banner">{error}</div> : null}

        <div className="details-hero">
          <div className="details-gallery">
            <img
              className="details-image"
              src={thumbnailImages[activeImageIndex] || thumbnailImages[0]}
              alt={property.title}
            />
            {thumbnailImages.length > 1 ? (
              <div className="details-thumbnails">
                {thumbnailImages.map((src, index) => (
                  <button
                    key={`${src.slice(0, 24)}-${index}`}
                    type="button"
                    className={`details-thumb ${index === activeImageIndex ? "active" : ""}`}
                    onClick={() => setActiveImageIndex(index)}
                    aria-label={`Show image ${index + 1}`}
                  >
                    <img src={src} alt={`${property.title} ${index + 1}`} />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="details-copy">
            <h1 className="details-title">{property.title}</h1>
            <div className="details-meta">
              {property.locationLabel}, {property.city} • {property.type} • {property.status}
            </div>
            <div className="details-price">₹ {property.price}</div>
            <p className="details-description">{property.description}</p>
            {canEditListing ? (
              <button
                className="details-edit-btn"
                type="button"
                onClick={() => setEditOpen(true)}
              >
                Edit Listing
              </button>
            ) : null}
          </div>
        </div>

        <div className="amenities">
          <span className="amenity">Type: {property.type}</span>
          <span className="amenity">Status: {property.status}</span>
          {property.type === "house" ? <span className="amenity">Bedrooms: {property.bedrooms}</span> : null}
          {property.type === "house" ? <span className="amenity">Bathrooms: {property.bathrooms}</span> : null}
          {(property.areaValue || property.areaSqft) ? (
            <span className="amenity">
              Area: {property.areaValue && property.areaUnit ? `${property.areaValue} ${property.areaUnit}` : `${property.areaSqft} sq ft`}
            </span>
          ) : null}
          {property.type === "shop" ? <span className="amenity">Units: {property.shopUnits}</span> : null}
        </div>

        <div className="map-panel">
          <h3>Location</h3>
          {canSeeMap && mapEmbedUrl ? (
            <iframe
              className="map-frame"
              title="property-map"
              loading="lazy"
              src={mapEmbedUrl}
              allowFullScreen
            />
          ) : (
            <div className="map-restricted">
              {canSeeMap
                ? "No coordinates available for this listing."
                : "Map is restricted for Common User role. Upgrade to Agent/Admin for location view."}
            </div>
          )}
        </div>

        <div className="inquiry-panel">
          <h3>Contact for Inquiry</h3>
          {isAuthenticated ? (
            <>
              <p className="details-contact">
                Contact: {property.contactAdminEmail || "Shared after inquiry confirmation"}
              </p>
              <textarea
                className="form-input"
                value={inquiryMessage}
                onChange={(event) => setInquiryMessage(event.target.value)}
                placeholder="Add inquiry message (optional)"
              />
              <button
                className="btn btn-strong"
                type="button"
                onClick={onSendInquiry}
                disabled={inquiryLoading || !canSubmitInquiry}
              >
                {inquiryLoading
                  ? "Sending..."
                  : canSubmitInquiry
                    ? "Contact for Inquiry"
                    : "Inquiry Unavailable"}
              </button>
            </>
          ) : (
            <>
              <p className="details-contact">Login required to view contact details and phone number.</p>
              <button
                className="btn btn-strong"
                type="button"
                onClick={() => navigate("/login", { state: { from: `/property/${id}` } })}
              >
                Login to View Contact
              </button>
            </>
          )}
          {inquiryStatus ? <div className="status-text">{inquiryStatus}</div> : null}
        </div>
      </div>
      {canEditListing && editOpen ? (
        <Modal className="fy-modal--wide" onClose={() => setEditOpen(false)}>
          <AddProperty
            embedded
            editPropertyId={property.id}
            initialPrefill={editPrefill}
            initialMessage="Update property details and save changes."
            onSuccess={onEditSuccess}
            onClose={() => setEditOpen(false)}
          />
        </Modal>
      ) : null}
    </div>
  );
}
